import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Sparkles, ArrowRight, Heart, Wand2, Users, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};
const gradientFor = (k) => GRADIENT_MAP[k] || GRADIENT_MAP['cyan-magenta'];

export default function SoulSpotlight() {
  const [soul, setSoul] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/souls/spotlight`);
        if (r.ok) setSoul(await r.json());
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading || !soul) return null;

  return (
    <section
      id="soul-spotlight"
      data-testid="landing-soul-spotlight"
      className="relative py-24 md:py-28 bg-[#020202] text-white overflow-hidden"
    >
      {/* Ambient wash matching soul gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(ellipse at 30% 40%, ${gradientFor(soul.gradient).match(/#[0-9A-F]{6}/gi)?.[0] || '#00F0FF'}22 0%, transparent 55%),
                       radial-gradient(ellipse at 70% 70%, ${gradientFor(soul.gradient).match(/#[0-9A-F]{6}/gi)?.[1] || '#FF007F'}22 0%, transparent 55%)`,
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <div className="flex items-center gap-2 mb-8">
          <Trophy size={14} className="text-amber-300" />
          <span className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/60">
            SOUL of the Week
          </span>
          <span className="h-1 w-1 rounded-full bg-white/30" />
          <span className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/40">
            Rotates every Monday
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="grid md:grid-cols-2 gap-0 rounded-3xl border border-white/10 bg-black/40 backdrop-blur-xl overflow-hidden"
          data-testid="spotlight-card"
        >
          {/* Left — big gradient tile */}
          <div
            className="relative min-h-[320px] md:min-h-[420px] p-10 flex flex-col justify-between overflow-hidden"
            style={{ background: gradientFor(soul.gradient) }}
          >
            <div className="absolute inset-0 opacity-25 mix-blend-overlay pointer-events-none" style={{
              background: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.5), transparent 50%), radial-gradient(circle at 75% 75%, rgba(0,0,0,0.35), transparent 50%)',
            }} />
            <div className="relative flex items-center gap-3">
              <Star size={14} className="text-amber-300" fill="currentColor" />
              <span className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-black/70 font-semibold">
                Featured this week
              </span>
            </div>
            <div className="relative">
              <div className="h-32 w-32 md:h-40 md:w-40 rounded-3xl bg-black/40 backdrop-blur-md border-2 border-white/20 flex items-center justify-center text-7xl md:text-8xl shadow-2xl">
                {soul.emoji || (soul.name || 'S').slice(0, 1)}
              </div>
            </div>
          </div>

          {/* Right — copy */}
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">
              SOUL of the Week
            </div>
            <h2
              className="mt-3 font-display font-black tracking-[-0.03em] text-4xl md:text-5xl leading-[1.05]"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 55%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {soul.name}
            </h2>
            <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
              {soul.tagline}
            </p>

            <div className="mt-5 flex items-center gap-3 text-[12px] text-white/55">
              <span className="inline-flex items-center gap-1"><Users size={11} /> by{' '}
                {soul.author_handle ? (
                  <Link to={`/@${soul.author_handle}`} className="text-white hover:text-cyan-300 transition">
                    {soul.author_name}
                  </Link>
                ) : (
                  <span className="text-white">{soul.author_name}</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1"><Heart size={11} /> {soul.likes}</span>
              <span className="inline-flex items-center gap-1"><Wand2 size={11} /> {soul.uses}</span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={`/souls/${soul.slug}`}
                data-testid="spotlight-open-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-full text-black font-semibold text-[14px] px-5 py-3 hover:scale-[1.02] transition"
                style={{ background: gradientFor(soul.gradient) }}
              >
                Open this SOUL <ArrowRight size={13} />
              </Link>
              <Link
                to="/souls"
                data-testid="spotlight-browse-btn"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-white text-[14px] font-medium px-5 py-3 transition"
              >
                <Sparkles size={13} /> Browse all souls
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
